import equal from "fast-deep-equal";
import React, { useEffect, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { bindActionCreators, Dispatch } from "redux";
import styled from "styled-components";
import { EventTypes, Venue, VenueTypes } from "sweet-global";
import NextButton from "sweet-web/components/universal/Button/NextButton";
import Input from "sweet-web/components/universal/Input";
import LocationInput from "sweet-web/components/universal/Input/LocationInput";
import MultiSelectInput from "sweet-web/components/universal/Input/MultiSelectInput";
import PhoneNumberInput from "sweet-web/components/universal/Input/PhoneNumberInput";
import { Label, SectionTitle } from "sweet-web/components/universal/Text";
import { Section, Wrapper } from "sweet-web/components/universal/View";
import { RootState } from "sweet-web/redux";
import * as selectors from "sweet-web/redux/selectors";
import { UpdateVenue } from "sweet-web/redux/venue/actions";

type Props = RouteComponentProps & ConnectedProps<typeof connector>;

const Information: React.FC<Props> = props => {
  const { venueId } = props.match.params as { venueId: string };
  const { venueEntity, loading } = props;

  const [venue, setVenue] = useState(venueEntity);

  useEffect(() => {
    setVenue(venueEntity);
  }, [venueEntity]);

  const next = async () => {
    await props.UpdateVenue(venueId, venue);
    props.history.push(`/listing/${venueId}/spaces`);
  };

  const save = async (update?: Partial<Venue>) => {
    if (update) {
      setVenue(v => ({
        ...v,
        ...update
      }));
    }
    await props.UpdateVenue(venueId, update ? update : venue);
  };

  return (
    <Wrapper>
      <Section>
        <SectionTitle>Title and Description</SectionTitle>

        <Input
          title={"Listing Title"}
          description="A short but punchy name for your venue"
          value={venue.name}
          onChange={value => {
            setVenue({
              ...venue,
              name: value
            });
          }}
          onBlur={() => save()}
        />

        <Input
          title={"Description"}
          description="Highlight what makes your venue special"
          value={venue.description}
          multiline={true}
          onChange={value => {
            setVenue({
              ...venue,
              description: value
            });
          }}
          onBlur={() => save()}
        />
      </Section>

      <Section>
        <SectionTitle>Location</SectionTitle>

        <LocationInput
          title="Street Address"
          description="Where your venue is located"
          location={venue.location}
          type="address"
          onLocationChange={value => {
            const newLocationValue = {
              ...venue.location,
              ...value
            };
            setVenue(v => ({
              ...v,
              location: newLocationValue
            }));
            save({ location: newLocationValue });
          }}
        />

        <Input
          title={"Location Details"}
          description="Any other information customers need to know about your venue's location"
          value={venue.location.description}
          multiline={true}
          onChange={value => {
            setVenue(v => ({
              ...v,
              location: {
                ...v.location,
                description: value
              }
            }));
          }}
          onBlur={() => save()}
        />
      </Section>

      <Section>
        <SectionTitle>Contact</SectionTitle>
        <PhoneNumberInput
          title={"Venue Phone Number"}
          value={venue.phoneNumber}
          onChange={value => {
            setVenue({
              ...venue,
              phoneNumber: value
            });
          }}
          onBlur={() => save()}
        />
        <Input
          title={"Email Address"}
          value={venue.emailAddress}
          onChange={value => {
            setVenue({
              ...venue,
              emailAddress: value
            });
          }}
          onBlur={() => save()}
        />
        <Input
          title={"Website"}
          value={venue.website}
          onChange={value => {
            setVenue({
              ...venue,
              website: value
            });
          }}
          onBlur={() => save()}
        />
      </Section>

      <Section>
        <SectionTitle style={{ marginBottom: 5 }}>Venue Type</SectionTitle>
        <Label>Categorize your venue</Label>

        <MultiSelectInput
          name="list-venue-type"
          options={VenueTypes}
          value={venue.type}
          onChange={value => save({ type: value })}
        />
      </Section>

      <Section>
        <SectionTitle style={{ marginBottom: 5 }}>Event Types</SectionTitle>
        <Label>Select all the types of events that your venue could host</Label>

        <MultiSelectInput
          name="list-venue-event-types"
          options={EventTypes}
          value={venue.eventTypes}
          onChange={value => save({ eventTypes: value })}
        />
      </Section>

      <NextButton primary={true} loading={loading} onClick={next} />
    </Wrapper>
  );
};

const mapState = (state: RootState, ownProps: RouteComponentProps) => {
  const { venueId } = ownProps.match.params as { venueId: string };

  return {
    venueEntity: selectors.getVenue(venueId)(state),
    loading: selectors.isUpdatingVenue(state)
  };
};
const mapDispatch = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      UpdateVenue
    },
    dispatch
  );
const connector = connect(mapState, mapDispatch);

export default connector(Information);
